import {
    ExpenseGet,
    ExpenseStatus,
    ISubscription,
    Receipt,
    SubscribeState,
    Ulid,
} from "@kase/my-marathon-communication";
import { computed, signal } from "@preact/signals";
import { startOfDay } from "date-fns";
import { store } from "~/core/store";
import { dateFromApi, omit, upsert, upsertOne } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import { getAttachment, loadThumbnails } from "./attachment";
import { makeDuplicate } from "./duplicate";
import {
    AttachmentProcess,
    CreateExpenseRequest,
    Expense,
    ExpenseReceipt,
    ExpenseType,
    ExpenseViewStatus,
    ProcessSubscriber,
    ReceiptAttachmentRef,
    SpsCode,
    SpsCodeMapItem,
    UpdateExpenseRequest,
    UpdateReceiptRequest,
} from "./interfaces";
import { makeOCR } from "./ocr";
import { makeReceipt } from "./receipt";

export const PAID_UNKNOWN = 1;

const getViewStatus = (expense: ExpenseGet) => {
    if (
        expense.status === ExpenseStatus.Deleted ||
        expense.status === ExpenseStatus.UNRECOGNIZED
    ) {
        return ExpenseViewStatus.Deleted;
    }

    if (expense.status === ExpenseStatus.Attest) {
        return ExpenseViewStatus.Approved;
    }

    if (expense.status === ExpenseStatus.NotReady) {
        return ExpenseViewStatus.NotSubmitted;
    }

    if (
        expense.status === ExpenseStatus.Ready ||
        expense.status === ExpenseStatus.Printed
    ) {
        return ExpenseViewStatus.Submitted;
    }

    if (expense.paid_out_date === PAID_UNKNOWN) {
        return ExpenseViewStatus.BookedPaid;
    }

    const today = startOfDay(new Date());
    const paidDate = expense.paid_out_date
        ? dateFromApi(expense.paid_out_date)
        : null;

    if (paidDate && paidDate < today) {
        return ExpenseViewStatus.BookedPaid;
    }

    return ExpenseViewStatus.BookedUnpaid;
};

export const makeExpense = ({ connection, company }: ApiSliceParams) => {
    const allTypes = signal<ExpenseType[]>([]);
    const types = computed(() => allTypes.value.filter((t) => t.active));
    const attachmentRefs = signal<ReceiptAttachmentRef[]>([]);
    const spsCodes = signal<SpsCode[]>([]);
    const spsCodeMap = signal<SpsCodeMapItem[]>([]);
    const spsLoading = signal(false);
    const spsLoaded = signal(false);
    const typeLoading = signal(false);
    const typeLoaded = signal(false);
    const subscribers: Record<string, ProcessSubscriber> = {};
    const openState: Record<string, boolean> = {};
    const applyState: Record<string, boolean> = {};
    const previewState: Record<string, boolean> = {};
    const uploadedReceiptIds: string[] = [];
    const duplicate = makeDuplicate({ connection, company });
    const ocr = makeOCR({
        connection,
        company,
        mapCode: (codes) => {
            for (const code of codes) {
                const typeCode = spsCodeMap
                    .peek()
                    .find((m) => m.spsc === code)?.expensetype;

                if (!typeCode) {
                    continue;
                }

                const type = allTypes.peek().find((t) => t.code === typeCode);

                if (type) {
                    return type;
                }
            }

            return null;
        },
    });
    const { on, off, emit } = createEmitter<Expense>();

    const view = (expense: ExpenseGet) => {
        const finalExpense: Expense = Object.assign(expense, {
            id: expense.id.toString(),
            _id: expense.id,
            _status: expense.status,
            status: getViewStatus(expense),
            receipts: signal([]),
            loading: signal(false),
            loaded: signal(false),
        });

        return finalExpense;
    };

    const {
        listen,
        allLoading: loading,
        allLoaded: loaded,
        loaded: getExpenseLoaded,
        items: expenses,
        get,
    } = createListController<ExpenseGet, Expense>({
        connection,
        list: (con) => con.expense.items({ company: company.value }),
        view,
        emit,
        sort: (a, b) =>
            dateFromApi(b.date).getTime() - dateFromApi(a.date).getTime() ||
            b.id.localeCompare(a.id),
        name: "expense",
    });
    const loadedApi = getExpenseLoaded(undefined, true);
    const receipt = makeReceipt({
        connection,
        company,
        subscribers,
        expenses,
        uploadedReceiptIds,
    });

    const loadAttachments = async (
        expenseId: string,
        receipt: ExpenseReceipt,
        previous: readonly Readonly<Ulid>[] = [],
    ) => {
        const newAttachments = receipt._attachments.filter(
            (id) => !previous.some((p) => p.equals(id)),
        );

        if (newAttachments.length === 0) {
            return;
        }

        const con = connection.value;
        const promises = newAttachments.map(async (id) => {
            const attachment = await getAttachment(
                con,
                company.value,
                receipt._id,
                id,
            );

            if (previewState[expenseId]) {
                loadThumbnails(attachment);
            }

            receipt.attachments.value = upsertOne(
                receipt.attachments.peek().slice(),
                attachment,
                "id",
            );
            attachmentRefs.value = upsertOne(
                attachmentRefs.peek().slice(),
                {
                    id: attachment.id,
                    blob_id: attachment.blob_id,
                    attachmentId: attachment.id,
                    receiptId: receipt.id,
                    expenseId,
                },
                "id",
            );
        });

        const result = await Promise.allSettled(promises);

        result
            .filter((r) => r.status === "rejected")
            .forEach((r) => {
                console.log(
                    "[expense.loadAttachments] Failed to load attachment",
                    r,
                );
            });

        receipt.loading.value = false;
        receipt.tag.value = receipt.tag.peek() + 1;
    };

    const listenExpense = async (
        expense: Expense,
        subscription: ISubscription<Receipt, Receipt>,
    ) => {
        expense.loading.value = true;

        for await (const event of subscription) {
            if (event.kind === SubscribeState.Remove) {
                const newReceipts = expense.receipts.peek().slice();
                const newRefs = attachmentRefs
                    .peek()
                    .filter(
                        (ref) =>
                            !event.values.find(
                                (v) => v.id.toString() === ref.receiptId,
                            ),
                    );

                for (const item of event.values) {
                    const idx = newReceipts.findIndex((r) =>
                        r._id.equals(item.id),
                    );

                    if (idx !== -1) {
                        const [receipt] = newReceipts.splice(idx, 1);

                        receipt.attachments.peek().forEach((a) => {
                            delete applyState[a.id];
                            ocr.forget(a.id);
                        });
                    }
                }

                expense.receipts.value = newReceipts;
                attachmentRefs.value = newRefs;

                if (Object.keys(subscribers).length > 0) {
                    for (const item of event.values) {
                        delete subscribers[item.id.toString()];
                    }
                }
            } else {
                const result = event.values.map((v) => receipt.view(v));
                const newReceipts = expense.receipts.peek().slice();

                for (const item of result) {
                    const existingIdx = newReceipts.findIndex((r) =>
                        r._id.equals(item._id),
                    );

                    if (existingIdx === -1) {
                        newReceipts.push(item);
                        loadAttachments(expense.id, item);
                    } else {
                        const oldAttachments =
                            newReceipts[existingIdx]._attachments;

                        Object.assign(
                            newReceipts[existingIdx],
                            omit(
                                item,
                                "attachments",
                                "loading",
                                "tag",
                                "_id",
                                "id",
                            ),
                        );
                        loadAttachments(
                            expense.id,
                            newReceipts[existingIdx],
                            oldAttachments,
                        );
                        newReceipts[existingIdx].tag.value =
                            newReceipts[existingIdx].tag.peek() + 1;
                    }
                }

                expense.receipts.value = newReceipts;

                if (Object.keys(subscribers).length > 0) {
                    for (const item of result) {
                        const subscriber = subscribers[item.id];

                        if (subscriber) {
                            const newItems = item.attachments
                                .peek()
                                .filter(
                                    (a) => !subscriber.skipIds.includes(a.id),
                                );

                            subscriber.skipIds.push(
                                ...newItems.map((a) => a.id),
                            );
                            newItems.forEach((a) =>
                                ocr.process(a.blob_id, a.id),
                            );
                        }
                    }
                }

                if (
                    event.kind === SubscribeState.LoadedCache ||
                    event.kind === SubscribeState.Loaded
                ) {
                    expense.loaded.value = true;
                    expense.loading.value = false;
                }
            }
        }
    };

    const open = (expense: Expense) => {
        if (openState[expense.id]) {
            return;
        }

        const con = connection.value;
        const sub = con.expense.open({
            id: expense._id,
            company: company.value,
        });

        listenExpense(expense, sub);
        openState[expense.id] = true;

        return () => sub.unsub();
    };

    const preview = (expense: Expense) => {
        if (previewState[expense.id]) {
            return;
        }

        previewState[expense.id] = true;
        expense.receipts
            .peek()
            .flatMap((r) => r.attachments.peek())
            .forEach(loadThumbnails);
    };

    const create = async ({
        description = "New Expense",
        status = ExpenseStatus.NotReady,
    }: CreateExpenseRequest = {}) => {
        const con = connection.value;

        return con.expense.create({
            company: company.value,
            description,
            status,
        });
    };

    const update = async ({
        id,
        description,
        status,
    }: UpdateExpenseRequest) => {
        const con = connection.value;

        return con.expense.update({
            id,
            company: company.value,
            description,
            status,
        });
    };

    const remove = async (id: Readonly<Ulid>) => {
        const con = connection.value;

        return con.expense.delete({ id, company: company.value });
    };

    const loadTypes = async () => {
        const con = connection.value;

        typeLoading.value = true;

        for await (const event of con.expensetype.items({
            company: company.value,
        })) {
            if (event.kind === SubscribeState.Remove) {
                allTypes.value = allTypes
                    .peek()
                    .filter(
                        (t) => !event.values.find((v) => v.id.equals(t._id)),
                    );
            } else {
                const newItems = event.values.map((item) => ({
                    ...item,
                    _id: item.id,
                    id: item.id.toString(),
                }));

                allTypes.value = upsert(
                    allTypes.peek().slice(),
                    newItems,
                    "id",
                );

                if (event.kind === SubscribeState.Loaded) {
                    typeLoading.value = false;
                    typeLoaded.value = true;
                    break;
                }
            }
        }
    };

    const loadSpsCodes = async () => {
        const con = connection.value;

        for await (const event of con.spsc.items()) {
            if (event.kind === SubscribeState.Remove) {
                spsCodes.value = spsCodes
                    .peek()
                    .filter((c) => !event.values.find((v) => v.id === c.id));
            } else {
                spsCodes.value = upsert(
                    spsCodes.peek().slice(),
                    event.values,
                    "id",
                );

                if (event.kind === SubscribeState.Loaded) {
                    break;
                }
            }
        }
    };

    const loadSpsMap = async () => {
        const con = connection.value;

        for await (const event of con.spscexpensetype.items({
            company: company.value,
        })) {
            if (event.kind === SubscribeState.Remove) {
                spsCodeMap.value = spsCodeMap
                    .peek()
                    .filter(
                        (c) => !event.values.find((v) => v.id.equals(c._id)),
                    );
            } else {
                const newItems = event.values.map((item) => ({
                    ...item,
                    _id: item.id,
                    id: item.id.toString(),
                }));

                spsCodeMap.value = upsert(
                    spsCodeMap.peek().slice(),
                    newItems,
                    "id",
                );

                if (event.kind === SubscribeState.Loaded) {
                    break;
                }
            }
        }
    };

    const loadSps = async () => {
        spsLoading.value = true;

        await Promise.all([loadSpsCodes(), loadSpsMap()]);

        spsLoading.value = false;
        spsLoaded.value = true;
    };

    const mapCodes = (codes: number[]) => {
        const result: ExpenseType[] = [];

        for (const code of codes) {
            const typeCode = spsCodeMap
                .peek()
                .find((m) => m.spsc === code)?.expensetype;

            if (!typeCode) {
                continue;
            }

            const type = allTypes.peek().find((t) => t.code === typeCode);

            if (type) {
                result.push(type);
            }
        }

        return result;
    };

    const once = async (id: Readonly<Ulid>) => {
        const existing = expenses.peek().find((e) => e._id.equals(id));

        if (existing) {
            return existing;
        }

        return new Promise<Expense>((resolve) => {
            const listener = (newItems: Expense[]) => {
                const item = newItems.find((e) => e._id.equals(id));

                if (item) {
                    off("add", listener);
                    resolve(item);
                }
            };

            on("add", listener);
        });
    };

    const getType = (id: string | null | undefined | Readonly<Ulid>) => {
        if (!id) {
            return null;
        }

        const finalId = typeof id === "string" ? id : id.toString();

        return allTypes.peek().find((e) => e.id === finalId) ?? null;
    };

    const applyProcessData = (processData: AttachmentProcess) => {
        if (applyState[processData.attachmentId]) {
            return;
        }

        applyState[processData.attachmentId] = true;

        const { api } = store;
        const ref = attachmentRefs
            .peek()
            .find((i) => i.attachmentId === processData.attachmentId);
        const tExpense = expenses.peek().find((i) => i.id === ref?.expenseId);
        const tReceipt = tExpense?.receipts
            .peek()
            .find((i) => i.id === ref?.receiptId);

        if (!tReceipt) {
            return;
        }

        const isOcrDesc =
            api.settings.user.store.peek().expense.ocr_category_as_description;
        const { figuresMap, type } = processData;
        const updateObj: Omit<UpdateReceiptRequest, "id"> = {};
        const touched =
            tReceipt.amount.toNumber() ||
            tReceipt.base_vat.toNumber() ||
            tReceipt.comment;

        if (type) {
            updateObj.expense_type = type._id;
        }

        if (figuresMap.currency) {
            const currency = api.currency.getByCode(figuresMap.currency.value);

            if (currency) {
                updateObj.currency = currency._id;
            }
        }

        if (figuresMap.category && !tReceipt.comment && isOcrDesc) {
            updateObj.comment = figuresMap.category.value;
        }

        if (figuresMap.date) {
            updateObj.date = new Date(figuresMap.date.value);
        }

        if (figuresMap.vat && !tReceipt.base_vat.toNumber()) {
            updateObj.base_vat = figuresMap.vat.value;
        }

        if (figuresMap.total && !tReceipt.amount.toNumber()) {
            updateObj.amount = figuresMap.total.value;
        }

        if (
            Object.keys(updateObj).length > 0 &&
            (uploadedReceiptIds.indexOf(tReceipt.id) !== -1 || !touched)
        ) {
            receipt.update({
                id: tReceipt._id,
                ...updateObj,
            });
        }
    };

    return {
        duplicate,
        receipt,
        ocr,
        attachmentRefs,
        expenses,
        types,
        loading,
        spsLoading,
        typeLoading,
        loaded,
        loadedApi,
        create,
        update,
        remove,
        loadTypes,
        loadSps,
        mapCodes,
        once,
        listen,
        get,
        getType,
        open,
        preview,
        applyProcessData,
    };
};
